import React, { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';

type Photo = {
  id: string;
  filename: string;
  content_type: string;
  image_url: string;
  created_at: string;
}

type PhotoGalleryViewerProps = {
  photos: Photo[];
  activeIndex: number;
  visible: boolean;
  onHide: () => void;
  onActiveIndexChange: (index: number) => void;
}

const PhotoGalleryViewer: FC<PhotoGalleryViewerProps> = ({ photos, activeIndex, visible, onHide, onActiveIndexChange }) => {
  const goPrev = () => {
    onActiveIndexChange(activeIndex === 0 ? photos.length - 1 : activeIndex - 1);
  };

  const goNext = () => {
    onActiveIndexChange(activeIndex === photos.length - 1 ? 0 : activeIndex + 1);
  };

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onHide();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ visible, activeIndex, photos.length ]);

  if (!visible || photos.length === 0) return null;

  const photo = photos[activeIndex];

  return createPortal(
    <div className='fixed inset-0 z-[1000] bg-black/90 flex flex-col items-center justify-center'>
      <button
        type='button'
        onClick={onHide}
        className='absolute top-4 right-4 text-white text-3xl leading-none hover:text-gray-300'
      >
        &times;
      </button>

      {photos.length > 1 && (
        <button
          type='button'
          onClick={goPrev}
          className='absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl leading-none hover:text-gray-300 px-2'
        >
          &#8249;
        </button>
      )}

      <img
        src={photo.image_url}
        alt={photo.filename}
        className='max-h-[80vh] max-w-[90vw] object-contain'
      />

      {photos.length > 1 && (
        <button
          type='button'
          onClick={goNext}
          className='absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl leading-none hover:text-gray-300 px-2'
        >
          &#8250;
        </button>
      )}

      <p className='mt-4 text-white text-sm'>
        {activeIndex + 1} / {photos.length}
      </p>
    </div>,
    document.body
  );
};

export default PhotoGalleryViewer;
